import { C, Note, P, Terms } from '../prose';
import type { Topic } from '../types';

/** What is held back, who is disclosed for it, and how both are kept current. */
export const blockieren = {
  regeln: {
    group: 'blockieren',
    title: { de: 'Regeln', en: 'Rules' },
    teaser: {
      de: 'Was angehalten wird, woher eine Regel kommt und was gegen was gewinnt.',
      en: 'What gets held back, where a rule comes from, and what beats what.',
    },
    body: {
      de: (
        <>
          <P>
            Die Tabelle zeigt die <strong>geltenden</strong> Regeln, nicht nur Ihre eigenen: die
            Frage, mit der man hierher kommt, lautet „was blockiert meine Seite gerade“, und Ihre
            eigenen Regeln sind darauf nur ein Teil der Antwort.
          </P>
          <Terms
            items={[
              ['Plattform', 'Mitgeliefert und immer aktiv. Sie decken die verbreiteten Anbieter ab und lassen sich nicht entfernen.'],
              ['eigene', 'Von Ihnen ergänzt. Additiv — sie kommen dazu, sie ersetzen nichts.'],
              ['Erlauben', 'Gewinnt gegen Blockieren. Das ist die Ausnahme für den Fall, dass eine Plattform-Regel etwas trifft, das auf Ihrer Seite notwendig ist.'],
            ]}
          />
          <P>
            <strong>Klick-zum-Laden</strong> ersetzt eingebettete Inhalte — ein YouTube-Video, eine
            Karte — durch eine Fläche, die der Besucher selbst anklickt. Das ist der saubere Weg
            für Einbettungen, die ohne Einwilligung nicht laden dürfen, aber sichtbar bleiben
            sollen.
          </P>
          <Note>
            Änderungen wirken erst nach dem nächsten Veröffentlichen. Eine hier entfernte Regel
            blockiert weiter, bis das Banner neu veröffentlicht wurde.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            The table shows the <strong>effective</strong> rules, not only your own: the question
            people arrive with is "what is my site blocking right now", and your own rules are one
            contribution to that answer.
          </P>
          <Terms
            items={[
              ['Platform', 'Shipped with the product and always active. They cover the common vendors and cannot be removed.'],
              ['own', 'Added by you. Additive — they come on top, they replace nothing.'],
              ['Allow', 'Beats block. This is the exception for when a platform rule catches something your site genuinely needs.'],
            ]}
          />
          <P>
            <strong>Click-to-load</strong> replaces embedded content — a YouTube video, a map —
            with a surface the visitor clicks themselves. It is the clean route for embeds that
            may not load without consent but should stay visible.
          </P>
          <Note>
            Changes take effect at the next publish. A rule removed here keeps blocking until the
            banner has been published again.
          </Note>
        </>
      ),
    },
  },

  anbieter: {
    group: 'blockieren',
    title: { de: 'Anbieter', en: 'Vendors' },
    teaser: {
      de: 'Wer im Auswahl-Dialog als Empfänger genannt wird — und warum das Pflicht ist.',
      en: 'Who is named as a recipient in the dialog — and why naming them is required.',
    },
    body: {
      de: (
        <>
          <P>
            Art. 13 DSGVO verlangt, dass die Empfänger der Daten benannt werden. Diese Liste ist
            diese Benennung: sie erscheint im Auswahl-Dialog und speist die Cookie-Erklärung.
          </P>
          <P>
            Ein Anbieter, der geladen wird und hier fehlt, ist ein nicht genannter Empfänger. Ein
            Anbieter, der hier steht und nicht mehr geladen wird, ist eine falsche Auskunft. Beide
            Richtungen sind Fehler, und beide entstehen still — deshalb der Scan.
          </P>
          <Terms
            items={[
              ['Schlüssel', 'Bleibt stabil, auch wenn der Name sich ändert. Er verbindet den Eintrag mit den Funden des Scanners.'],
              ['Speicherdauer', 'Wie lange der Anbieter die Daten hält. Steht in dessen Datenschutzerklärung, nicht bei uns.'],
              ['Kategorie', 'Entscheidet, ob der Anbieter vor einer Einwilligung laden darf. „Notwendig“ heißt: immer.'],
            ]}
          />
        </>
      ),
      en: (
        <>
          <P>
            Art. 13 GDPR requires that the recipients of the data be named. This list is that
            naming: it appears in the preferences dialog and feeds the cookie declaration.
          </P>
          <P>
            A vendor that loads and is missing here is an undisclosed recipient. A vendor listed
            here that no longer loads is a false statement. Both directions are errors, and both
            happen quietly — which is what the scan is for.
          </P>
          <Terms
            items={[
              ['Key', 'Stays stable even when the name changes. It ties the entry to the scanner findings.'],
              ['Retention', 'How long the vendor keeps the data. It comes from their privacy policy, not from us.'],
              ['Category', 'Decides whether the vendor may load before consent. "Necessary" means always.'],
            ]}
          />
        </>
      ),
    },
  },

  scan: {
    group: 'blockieren',
    title: { de: 'Scan', en: 'Scan' },
    teaser: {
      de: 'Was zweimaliges Laden zeigt, und warum der Scanner nichts allein entscheidet.',
      en: 'What loading the page twice reveals, and why the scanner decides nothing on its own.',
    },
    body: {
      de: (
        <>
          <P>
            Der Scan lädt Ihre Seite zweimal: einmal ohne Einwilligung, einmal mit. Die erste
            Runde ist die wichtige — was dort feuert, feuert trotz Banner, und das ist die
            eigentliche Compliance-Lücke. Alles andere ist Inventar.
          </P>
          <P>
            Anfragen werden dabei beobachtet und im Browser gestoppt, der Scan landet also nicht
            als Besuch in Ihrer Auswertung.
          </P>
          <P>
            Gefundene Anbieter werden <strong>vorgeschlagen</strong>, nicht übernommen. Eine
            falsche automatische Einordnung wäre ein rechtliches Risiko: „notwendig“ heißt, dass
            etwas ohne Einwilligung laufen darf. Solange ein Vorschlag offen ist, fehlt der
            Anbieter im Auswahl-Dialog als Empfänger — offene Vorschläge sind deshalb keine
            Aufgabe für später.
          </P>
          <Note>
            <strong>Wöchentlich scannen</strong> ist der Punkt, an dem das Ganze nützlich wird:
            Seiten bekommen zwischen Ihren Besuchen neue Tags. Eine Mail geht nur raus, wenn
            tatsächlich neue Anbieter auftauchen.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            The scan loads your page twice: once without consent, once with. The first pass is the
            one that matters — whatever fires there fires despite the banner, and that is the
            actual compliance gap. Everything else is inventory.
          </P>
          <P>
            Requests are observed and stopped inside the browser, so the scan does not land in
            your analytics as a visit.
          </P>
          <P>
            Vendors that are found are <strong>proposed</strong>, not adopted. A wrong automatic
            categorisation would be a legal risk: "necessary" means something may run without
            consent. While a proposal is open, that vendor is missing from the dialog as a
            recipient — open proposals are not a task for later.
          </P>
          <Note>
            <strong>Weekly scanning</strong> is where this becomes useful: pages gain tags between
            your visits. Mail goes out only when new vendors actually appear.
          </Note>
        </>
      ),
    },
  },

  'cookie-erklaerung': {
    group: 'blockieren',
    title: { de: 'Cookie-Erklärung', en: 'Cookie declaration' },
    teaser: {
      de: 'Die Tabelle für die Datenschutzerklärung, aus den veröffentlichten Anbietern erzeugt.',
      en: 'The table for your privacy policy, generated from the published vendors.',
    },
    body: {
      de: (
        <>
          <P>
            Aufsichtsbehörden erwarten, dass die Datenschutzerklärung nennt, welche Cookies
            gesetzt werden, wie lange sie leben und wer die Daten bekommt. Von Hand bleibt das
            nicht aktuell — es ändert sich mit jedem Tag, den jemand hinzufügt.
          </P>
          <P>
            Zwei Wege hinaus: <strong>Als HTML kopieren</strong> für eine statische Tabelle ohne
            Klassen und Styles, die die Typografie Ihrer Seite erbt — und dieselbe Auskunft als
            öffentliches JSON unter <C>/v1/c/&lt;site_key&gt;/cookies.json</C> für alle, die sie
            selbst rendern wollen.
          </P>
          <Note>
            Kein Einbettungs-Skript. Ein Datenschutzprodukt sollte niemanden bitten, ein weiteres
            fremdes Skript auf die Seite zu legen, deren Skripte es kontrollieren soll.
          </Note>
        </>
      ),
      en: (
        <>
          <P>
            Supervisory authorities expect a privacy policy to name which cookies are set, how long
            they live and who receives the data. Maintained by hand that never stays current — it
            changes with every tag anyone adds.
          </P>
          <P>
            Two ways out: <strong>Copy as HTML</strong> gives a static table with no classes and no
            styles, inheriting your page's typography — and the same data is public JSON at
            <C>/v1/c/&lt;site_key&gt;/cookies.json</C> for anyone who would rather render it
            themselves.
          </P>
          <Note>
            No embed script. A privacy product should not ask you to add another third-party script
            to the page whose scripts it exists to control.
          </Note>
        </>
      ),
    },
  },
} as const satisfies Record<string, Topic>;
